/** @file writePose: writes a pose at one frame of a draft clip with its inverse and unknown-node warnings. */
import type { Ease, NodeKey } from '@zilo/pose3d-format/types/clip';
import type { CommandOutcome } from '../apply/commandDef';
import { checkClip } from '../puppetDoc/checkClip';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import { checkFrame } from './checkFrame';
import { keyAt } from './keyAt';
import { keyWarnings } from './keyWarnings';
import { restoreKeys } from './restoreKeys';
import { writeKey } from './writeKey';

/** How the pose lands: replacing the key's nodes (its events kept) or merging into them; an optional key ease. */
export type PoseWrite = { clipName: string; frame: number; nodes: Record<string, NodeKey>; merge: boolean; ease?: Ease };

/** Writes `w` into `draft.clips[w.clipName]`; result `{ frame }`, inverse: the previous key at that frame. */
export function writePose(draft: PuppetDoc, w: PoseWrite): CommandOutcome {
  const clip = draft.clips[w.clipName];
  if (clip === undefined) return { result: { frame: w.frame }, warnings: [], inverse: [] };
  checkFrame(clip, w.frame, '/input/frame');
  const inverse = [restoreKeys(w.clipName, clip, [w.frame])];
  const events = keyAt(clip, w.frame)?.events;
  const key = { frame: w.frame, nodes: structuredClone(w.nodes), ...(w.ease === undefined ? {} : { ease: w.ease }), ...(events === undefined || w.merge ? {} : { events }) };
  writeKey(clip, key, w.merge ? 'mergeNodes' : 'replace');
  checkClip(w.clipName, clip);
  return { result: { frame: w.frame }, warnings: keyWarnings(draft, w.clipName, clip, [key]), inverse };
}
